function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import "core-js/modules/es.array.sort.js";
import "core-js/modules/web.dom-collections.iterator.js";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { Group } from "@visx/group";
import { useParentSize } from "@visx/responsive";
import { scaleLinear, scaleTime } from "@visx/scale";
import { observer } from "mobx-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import ChartableMixin from "../../../ModelMixins/ChartableMixin";
import MappableMixin from "../../../ModelMixins/MappableMixin";
import LineChart from "./LineChart";
import Styles from "./chart-preview.scss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const defaultMargin = {
  top: 5,
  left: 5,
  right: 5,
  bottom: 5
};

/**
 * Chart component for feature info panel popup
 */
const FeatureInfoPanelChart = observer(props => {
  var _props$baseColor;
  const [loadingFailed, setLoadingFailed] = useState(false);
  const {
    t
  } = useTranslation();
  const parentSize = useParentSize();
  const width = props.width || Math.max(parentSize.width, 300) || 0;
  const height = props.height || Math.max(parentSize.height, 200) || 0;
  const catalogItem = props.item;

  // If a yColumn is specified, use it if it is of line type, otherwise use
  // the first line type chart item.
  let chartItem = props.yColumn ? catalogItem.chartItems.find(it => it.id === props.yColumn) : catalogItem.chartItems.find(isLineType);
  chartItem = chartItem && isLineType(chartItem) ? chartItem : undefined;
  const notChartable = !ChartableMixin.isMixedInto(catalogItem);
  const isLoading = !chartItem && MappableMixin.isMixedInto(catalogItem) && catalogItem.isLoadingMapItems;
  const noData = !chartItem || chartItem.points.length === 0;

  // Text to show when chart is not ready or available
  const chartStatus = notChartable ? "chart.noData" : isLoading ? "chart.loading" : loadingFailed ? "chart.noData" : noData ? "chart.noData" : undefined;
  const canShowChart = chartStatus === undefined;
  const margin = _objectSpread(_objectSpread({}, defaultMargin), props.margin);
  const baseColor = (_props$baseColor = props.baseColor) !== null && _props$baseColor !== void 0 ? _props$baseColor : "#efefef";
  useEffect(() => {
    if (MappableMixin.isMixedInto(catalogItem)) {
      catalogItem.loadMapItems().then(result => {
        setLoadingFailed(result.error !== undefined);
        result.logError();
      });
    } else {
      setLoadingFailed(false);
    }
  }, [catalogItem]);
  return /*#__PURE__*/_jsxs("div", {
    className: Styles.previewChart,
    ref: parentSize.parentRef,
    children: [!canShowChart && /*#__PURE__*/_jsx(ChartStatusText, {
      width: width,
      height: height,
      children: t(chartStatus)
    }), canShowChart && chartItem && /*#__PURE__*/_jsx(Chart, {
      width: width,
      height: height,
      margin: margin,
      chartItem: chartItem,
      baseColor: baseColor,
      xAxisLabel: props.xAxisLabel
    })]
  });
});
const isLineType = chartItem => chartItem.type === "line" || chartItem.type === "lineAndPoint";
/**
 * Private Chart component that renders the SVG chart
 */
const Chart = observer(_ref => {
  let {
    width,
    height,
    margin,
    chartItem,
    baseColor,
    xAxisLabel
  } = _ref;
  const xAxisHeight = 30;
  const yAxisWidth = 10;
  const plot = useMemo(() => {
    return {
      width: width - margin.left - margin.right,
      height: height - margin.top - margin.bottom - xAxisHeight
    };
  }, [width, height, margin]);
  const scales = useMemo(() => {
    const xScaleParams = {
      domain: chartItem.domain.x,
      range: [margin.left + yAxisWidth, plot.width]
    };
    const yScaleParams = {
      domain: chartItem.domain.y,
      range: [plot.height, 0]
    };
    return {
      x: chartItem.xAxis.scale === "linear" ? scaleLinear(xScaleParams) : scaleTime(xScaleParams),
      y: scaleLinear(yScaleParams)
    };
  }, [chartItem.domain.x, chartItem.domain.y, chartItem.xAxis.scale, margin.left, plot.height, plot.width]);
  const textStyle = {
    fill: baseColor,
    fontSize: 10,
    textAnchor: "middle",
    fontFamily: "Arial"
  };
  const chartLabel = xAxisLabel !== null && xAxisLabel !== void 0 ? xAxisLabel : defaultChartLabel({
    xName: chartItem.xAxis.name,
    xUnits: chartItem.xAxis.units,
    yName: chartItem.name,
    yUnits: chartItem.units
  });
  useEffect(() => {
    chartItem.points = chartItem.points.sort((a, b) => scales.x(a.x) - scales.x(b.x));
  });
  return /*#__PURE__*/_jsx("svg", {
    width: width,
    height: height,
    children: /*#__PURE__*/_jsxs(Group, {
      top: margin.top,
      left: margin.left,
      children: [/*#__PURE__*/_jsx(AxisBottom, {
        top: plot.height
        // .nice() rounds the scale so that the aprox beginning and
        // aprox end labels are shown
        // See: https://stackoverflow.com/questions/21753126/d3-js-starting-and-ending-tick
        ,
        scale: scales.x.nice(),
        numTicks: 4,
        stroke: "#a0a0a0",
        tickStroke: "#a0a0a0",
        tickLabelProps: (_value, i, ticks) => {
          // To prevent the first and last values from getting clipped,
          // we position the first label text to start at the tick position
          // and the last label text to finish at the tick position. For all
          // others, middle of the text will coincide with the tick position.
          const textAnchor = i === 0 ? "start" : i === ticks.length - 1 ? "end" : "middle";
          return _objectSpread(_objectSpread({}, textStyle), {}, {
            textAnchor
          });
        },
        label: chartLabel,
        labelOffset: 3,
        labelProps: {
          fill: baseColor,
          fontSize: 10,
          textAnchor: "middle",
          fontFamily: "Arial"
        }
      }), /*#__PURE__*/_jsx(AxisLeft, {
        scale: scales.y,
        numTicks: 4,
        stroke: "none",
        tickStroke: "none",
        tickLabelProps: () => _objectSpread(_objectSpread({}, textStyle), {}, {
          textAnchor: "start",
          dx: "1em",
          dy: "0"
        })
      }), /*#__PURE__*/_jsx(LineChart, {
        id: "featureInfoPanelChart-".concat(chartItem.name),
        chartItem: chartItem,
        scales: scales,
        color: baseColor
      })]
    })
  });
});
Chart.displayName = "Chart";
export const ChartStatusText = styled.div.withConfig({
  displayName: "FeatureInfoPanelChart__ChartStatusText",
  componentId: "sc-dg2zcr-0"
})(["display:flex;align-items:center;justify-content:center;width:", "px;height:", "px;"], p => p.width, p => p.height);
const defaultChartLabel = opts => "".concat(withUnits(opts.yName, opts.yUnits), " x ").concat(withUnits(opts.xName, opts.xUnits));
const withUnits = (name, units) => units ? "".concat(name, " (").concat(units, ")") : name;
export default FeatureInfoPanelChart;